import React, {
	useState,
	useMemo,
	useCallback,
} from 'react';
import { CargoContractSides } from '@shared/utils/constants';
import { useVoyage } from '@client/screens/fleet/VoyageDetailsScreen/components/VoyageProvider/VoyageProvider';
import Claims from './Claims';

type Props = {
	portCallId: number;
};

const getCargoTabs = (cargos: Array<any>) => {
	return cargos.map((c) => {
		const isVcIn = c.contractSide === CargoContractSides.VC_IN;

		return {
			id: c.id.toString(),
			key: c.id.toString(),
			label: isVcIn ? 'IN' : 'OUT',
		};
	});
};

/*
* Wraps Claims.tsx, and is used on the Port Call modal. Differs from ClaimsTab.tsx
* in that we only display the relevant cargoports for the given port call, and then
* display all cargos for that cargo port. On ClaimsTab.tsx the structure differs
* as is instead cargos -> cargoports.
*/
const ClaimsWrapper = ({ portCallId }: Props) => {
	const [selectedCargoPortId, setSelectedCargoPortId] = useState<string | undefined>();
	const [selectedTab, setSelectedTab] = useState<string | undefined>();
	const { cargos } = useVoyage();

	const cargoPorts = useMemo(() => {
		if (!cargos || portCallId === undefined) {
			return null;
		}

		return cargos
			.flatMap((c) => c.CargoPorts)
			.filter((cp) => cp.portCallId === portCallId);
	}, [cargos, portCallId]);

	const selectedCargo = useMemo(() => {
		if (!cargos || portCallId === undefined) {
			return null;
		}

		const portCallCargoPorts = cargos
			.flatMap((c) => c.CargoPorts)
			.filter((cp) => cp.portCallId === portCallId);

		const relevantCargos = cargos.filter(
			(c) => portCallCargoPorts.map((cp) => cp.cargoId).includes(c.id),
		);

		if (selectedTab) {
			const relevantCargo = relevantCargos.find((c) => c.id === Number(selectedTab));

			const relevantCargoPort = relevantCargo?.CargoPorts
				.find((cp) => cp.portCallId === portCallId);

			setSelectedCargoPortId(relevantCargoPort?.id.toString());

			return relevantCargo;
		}

		if (!selectedCargoPortId) {
			setSelectedCargoPortId(portCallCargoPorts[0].id.toString());
		}

		return relevantCargos[0];
	}, [cargos, portCallId, selectedCargoPortId, selectedTab]);

	const tabs = useMemo(() => {
		if (!selectedCargo || !cargos || !cargoPorts) {
			return [];
		}

		const portCallCargos = cargos
			.filter((c) => cargoPorts?.some((cp) => cp.cargoId === c.id))
			.sort((a, b) => a.id - b.id);

		return getCargoTabs(portCallCargos);
	}, [selectedCargo, cargos, cargoPorts]);

	const handleTabChange = useCallback((tab: string) => {
		setSelectedTab(tab);
	}, []);

	return (
		<Claims
			selectedCargo={selectedCargo || undefined}
			selectedCargoPortId={selectedCargoPortId}
			setSelectedCargoPortId={setSelectedCargoPortId}
			cargoPorts={cargoPorts}
			tabs={tabs}
			selectedTab={selectedTab}
			onTabChange={handleTabChange}
		/>
	);
};

export default ClaimsWrapper;
